import Phaser from "phaser";
import type { BigNumber } from "@yjsgames/bignumber";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT, ScrollableList } from "@yjsgames/phaser-ui";
import {
  DISABLED_BUTTON_ALPHA,
  DISABLED_BUTTON_BG_ALPHA,
  FONT_FAMILY,
  PRIMARY_BUTTON_STYLE,
  setButtonEnabled,
  TYPO,
} from "./uiTokens.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import {
  computeRebirthLayout,
  formatRebirthPointChipValue,
  BUTTON_WIDTH,
  CURRENCY_CHIP_H,
  CURRENCY_CHIP_ICON_SIZE,
  CURRENCY_CHIP_W,
} from "./rebirthLayout.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { TipCarouselView } from "./TipCarouselView.js";
import { MultiplierSelector } from "./MultiplierSelector.js";
import type { Multiplier } from "../systems/multiBuy.js";
import { formatBig } from "./formatNumber.js";

/** 환생 강화 1행의 표시 상태 — 문구 조립(소스 합성·분해)은 tabData가 t()/formatBig으로 완성해 전달. */
export interface RebirthStatRow {
  readonly statId: string;
  readonly level: number;
  /** 1줄 제목+레벨 ("공격력 Lv.N"). */
  readonly cellName: string;
  /** 2줄 소스 분해 ("스탯+3 · 환생+5 · 유물×1.3"). pointBonus는 빈 문자열(환생 단독 적립 보너스). */
  readonly breakdown: string;
  /** 3줄 현재 최종값 → 다음 예상값(환생 레벨 +1 기준). */
  readonly result: string;
  /** 강화 비용 (환생 포인트). maxed면 무시. */
  readonly cost: BigNumber;
  /** 현재 배수로 구매할 레벨 수. */
  readonly count: number;
  readonly affordable: boolean;
  readonly maxed: boolean;
}

export interface RebirthTabData {
  readonly rebirthPoint: number;
  /** 현재 환생 횟수 기준 환생 가능 최소 챕터 (누진). */
  readonly requiredChapter: number;
  /** 지금 환생 시 획득 예상 포인트 (기본 + 포인트보너스 강화). */
  readonly gainedIfRebirth: number;
  readonly gainedBasePoints: number;
  readonly gainedBonusPoints: number;
  readonly pointFormula: string;
  readonly canRebirth: boolean;
  /** 강화 6종 행 (STAT_IDS 순). */
  readonly rows: readonly RebirthStatRow[];
}

export interface RebirthTabViewOptions {
  readonly onRebirth: () => void;
  /** 환생 스탯 강화 (환생 포인트 소모). */
  readonly onUpgradeStat: (statId: string) => void;
  /** 배수(x1/x10/x100/xMax) 변경 — 호출자가 각 행 비용/구매 가능 즉시 갱신. */
  readonly onMultiplierChange: (mult: Multiplier) => void;
}

/** 표시 순서 = rebirthUpgrades.tsv statId 순서. 행 문구는 tabData가 statCell.* / statCell.result.pointBonus로 완성. */
const STAT_IDS = ["attack", "fireRate", "goldGain", "critChance", "critDamage", "pointBonus"] as const;
type RebirthStatId = (typeof STAT_IDS)[number];

/** 행 좌측 아이콘 — 스탯 5종은 StatUpgradeTabView와 동일 키 재사용(같은 스탯 의미), pointBonus만 전용. */
const REBIRTH_ICON_KEY: Record<RebirthStatId, string> = {
  attack: "icon_stat_attack",
  fireRate: "icon_stat_fire_rate",
  goldGain: "icon_stat_gold_gain",
  critChance: "icon_stat_crit_chance",
  critDamage: "icon_stat_crit_damage",
  pointBonus: "icon_stat_point_bonus",
};

/** 강화 버튼 라벨 — 비용(환생 포인트)을 버튼에 통합(스탯 탭 buyButtonLabel 양식). */
function rebirthBuyLabel(state?: RebirthStatRow): string {
  if (!state) return "";
  if (state.maxed) return t("rebirth.max");
  const cost = formatBig(state.cost);
  return state.count > 1 ? `${cost} x${state.count}` : cost;
}

const REBIRTH_BUTTON_REQUIRED_ICON_SIZE = 14;
const REBIRTH_BUTTON_ICON_LEADING_GAP = 2;
const REBIRTH_BUTTON_ICON_VALUE_GAP = 4;

const rebirthButtonPrefixLabel = (): string => `${t("rebirth.do")} (`;
const rebirthButtonRequiredLabel = (requiredChapter: number): string => `${requiredChapter})`;

interface RowObjects {
  readonly statId: RebirthStatId;
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly icon: Phaser.GameObjects.Image;
  /** 제목+레벨 (1줄). */
  readonly name: Phaser.GameObjects.Text;
  /** 소스 분해 (2줄). */
  readonly breakdown: Phaser.GameObjects.Text;
  /** 현재→다음 결과 (3줄). */
  readonly result: Phaser.GameObjects.Text;
  readonly button: NineSliceButton;
}

interface CurrencyChipObjects {
  readonly bg: Phaser.GameObjects.NineSlice;
  readonly icon: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

/**
 * 환생 탭 — 스크롤되는 영구 강화 6종(공격력/발사쿨타임/골드획득/치명타 확률·배수/환생포인트보너스, 환생 포인트 소모) +
 * 스크롤 영역 밖 하단 고정 "환생하기" 버튼. 각 행은 StatUpgradeTabView와 동일 양식 —
 * 행 프레임(rectangle) + 좌측 스탯 아이콘 + 컴팩트 3줄(제목+레벨 / 소스 분해 / 현재→다음 예상값) +
 * 우측 강화 버튼(비용 라벨 + 환생 포인트 통화 아이콘 통합, 만렙 시 MAX·아이콘 숨김).
 *
 * 행은 생성자에서 1회 생성해 scrollList에 addContent하고 update()는 텍스트/버튼 상태만 갱신한다
 * (clearContent 미사용 — NineSliceButton 재생성/재바인딩 회피). 드래그 없음 → freezeItem 불필요.
 * 배치: 세로 offset/행 간격은 rebirth.json, 좌우/RTL/줄바꿈폭(wrapWidth)은 computeRebirthLayout.
 */
export class RebirthTabView implements TabViewContract<RebirthTabData> {
  private readonly title: Phaser.GameObjects.Text;
  /** 제목 아래 팁 캐러셀 (재사용 컴포넌트, tab="rebirth"). */
  private readonly tipView: TipCarouselView;
  private readonly multSelector: MultiplierSelector;
  private readonly info: Phaser.GameObjects.Text;
  private readonly currencyChip: CurrencyChipObjects;
  private readonly scrollList: ScrollableList;
  private readonly rows: RowObjects[] = [];
  private readonly rebirthBtn: NineSliceButton;
  private readonly rebirthBtnPrefix: Phaser.GameObjects.Text;
  private readonly rebirthBtnRequiredIcon: Phaser.GameObjects.Image;
  private readonly rebirthBtnRequired: Phaser.GameObjects.Text;
  private lastRequiredChapter = 0;
  /** relayout(방향 전환)에서 computeRebirthLayout 재호출용 콘텐츠 영역. */
  private readonly rect: { x: number; y: number; width: number };

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    private readonly panelBottom: number,
    opts: RebirthTabViewOptions,
  ) {
    this.rect = { x, y, width };
    const dir = getTextDirection();
    const L = computeRebirthLayout({ x, y, width, height: 0 }, dir, STAT_IDS.length, this.panelBottom);
    const htox = L.headTextOriginX;
    const headAlign = dir === "rtl" ? "right" : "left";
    const rtox = L.rowTextOriginX;

    this.title = scene.add
      .text(L.title.x, L.title.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textSecondary,
      })
      .setOrigin(htox, 0)
      .setDepth(2);
    // 제목 아래 팁 캐러셀 — 환생 규칙 설명(2~3개 팁)을 랜덤 시작 + ◀/▶ 순환(재사용 컴포넌트, 전체폭).
    this.tipView = new TipCarouselView(scene, L.tip.x, L.tip.y, L.tip.width, L.tip.height, "rebirth");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);
    this.info = scene.add
      .text(L.info.x, L.info.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textSecondary,
        lineSpacing: 4,
        align: headAlign,
      })
      .setOrigin(htox, 0)
      .setDepth(2);
    this.currencyChip = {
      bg: scene.add
        .nineslice(
          L.currencyChip.bg.x,
          L.currencyChip.bg.y,
          "hud_currency_chip",
          undefined,
          CURRENCY_CHIP_W,
          CURRENCY_CHIP_H,
          10,
          10,
          9,
          9,
        )
        .setOrigin(0, 0)
        .setDepth(2),
      icon: scene.add
        .image(L.currencyChip.icon.x, L.currencyChip.icon.y, "icon_rebirth_point")
        .setDisplaySize(CURRENCY_CHIP_ICON_SIZE, CURRENCY_CHIP_ICON_SIZE)
        .setDepth(3),
      label: scene.add
        .text(L.currencyChip.label.x, L.currencyChip.label.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.hudText,
        })
        .setOrigin(L.currencyChip.label.originX, 0.5)
        .setDepth(3),
    };

    // 하단 고정 "환생하기" 버튼 (스크롤 영역 밖) — 탭 콘텐츠 패널 하단 중앙(방향 무관).
    this.rebirthBtn = new NineSliceButton(scene, L.rebirthBtn.x, L.rebirthBtn.y, {
      style: PRIMARY_BUTTON_STYLE,
      label: "",
      widthOverride: 196,
      heightOverride: 36,
    });
    this.rebirthBtnPrefix = scene.add
      .text(0, 0, "", PRIMARY_BUTTON_STYLE.textStyle)
      .setOrigin(0, 0.5);
    this.rebirthBtnRequiredIcon = scene.add
      .image(0, 0, "icon_stage")
      .setDisplaySize(REBIRTH_BUTTON_REQUIRED_ICON_SIZE, REBIRTH_BUTTON_REQUIRED_ICON_SIZE);
    this.rebirthBtnRequired = scene.add
      .text(0, 0, "", PRIMARY_BUTTON_STYLE.textStyle)
      .setOrigin(0, 0.5);
    this.rebirthBtn.add([this.rebirthBtnPrefix, this.rebirthBtnRequiredIcon, this.rebirthBtnRequired]);
    this.renderRebirthButtonLabel(0);
    // 환생 불가 상태의 거부 피드백(빨간 틴트+흔들림)·클릭 억제는 setButtonEnabled(update)가 버튼에 위임.
    this.rebirthBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onRebirth());
    scene.add.existing(this.rebirthBtn);

    this.multSelector = new MultiplierSelector(scene, L.multiplier.x, L.multiplier.y, {
      width,
      height: 26,
      dir,
      onChange: (mult) => opts.onMultiplierChange(mult),
    });

    // 스크롤 영역 — 정보 아래 ~ 하단 버튼 위 (스크롤바 시각성은 스탯 탭과 동일 토큰).
    this.scrollList = new ScrollableList(scene, L.scroll.x, L.scroll.y, {
      width: L.scroll.width,
      height: L.scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);

    // 스크롤 내부 행 — 로컬 좌표(computeRebirthLayout rows). RTL이면 텍스트 우측, 버튼/아이콘 좌측 미러.
    STAT_IDS.forEach((statId, i) => {
      const r = L.rows[i]!;
      const frame = scene.add
        .rectangle(0, 0, r.frame.width, r.frame.height, PALETTE_NUM.cellBg, 0.88)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28)
        .setDepth(1);
      const icon = scene.add
        .image(0, 0, REBIRTH_ICON_KEY[statId])
        .setDisplaySize(20, 20)
        .setDepth(3);
      // 3줄 텍스트(제목/분해/결과) — wrapWidth로 우측 버튼 좌측 경계 침범 차단.
      const name = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.label, color: PALETTE.textPrimary })
        .setOrigin(rtox, 0.5)
        .setWordWrapWidth(r.wrapWidth);
      const breakdown = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textDim })
        .setOrigin(rtox, 0.5)
        .setWordWrapWidth(r.wrapWidth);
      const result = scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary })
        .setOrigin(rtox, 0.5)
        .setWordWrapWidth(r.wrapWidth);
      const button = new NineSliceButton(scene, 0, 0, {
        style: PRIMARY_BUTTON_STYLE,
        label: rebirthBuyLabel(),
        widthOverride: BUTTON_WIDTH,
        heightOverride: 34,
        iconKey: "icon_rebirth_point",
        iconSize: 12,
      });
      button.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onUpgradeStat(statId));

      this.scrollList.addContent(frame, r.frame.x, r.frame.y);
      this.scrollList.addContent(icon, r.icon.x, r.icon.y);
      this.scrollList.addContent(name, r.name.x, r.name.y);
      this.scrollList.addContent(breakdown, r.breakdown.x, r.breakdown.y);
      this.scrollList.addContent(result, r.result.x, r.result.y);
      this.scrollList.addContent(button, r.button.x, r.button.y);
      this.rows.push({ statId, frame, icon, name, breakdown, result, button });
    });
    this.scrollList.setContentHeight(L.contentHeight);
  }

  update(d: RebirthTabData): void {
    this.lastRequiredChapter = d.requiredChapter;
    this.info.setText(
      [
        d.gainedBonusPoints > 0
          ? t("rebirth.infoGainBreakdown", { gained: d.gainedIfRebirth, base: d.gainedBasePoints, bonus: d.gainedBonusPoints })
          : t("rebirth.infoGainBase", { gained: d.gainedIfRebirth, base: d.gainedBasePoints }),
        t("rebirth.infoGainFormula", { formula: d.pointFormula }),
      ].join("\n"),
    );
    this.renderRebirthButtonLabel(d.requiredChapter);
    this.currencyChip.label.setText(formatRebirthPointChipValue(d.rebirthPoint));
    for (const row of this.rows) {
      const state = d.rows.find((r) => r.statId === row.statId);
      if (!state) continue;
      row.name.setText(state.cellName);
      row.breakdown.setText(state.breakdown);
      row.result.setText(state.result);
      // 비용은 버튼 라벨에 통합 — 만렙은 MAX + 통화 아이콘 숨김 (스탯 탭과 동일 양식).
      row.button.setLabel(rebirthBuyLabel(state));
      row.button.setIconVisible(!state.maxed);
      setButtonEnabled(row.button, state.affordable && !state.maxed);
    }
    this.setRebirthButtonAvailable(d.canRebirth);
  }

  /** 현재 선택 배수 — boot가 upgradeRebirthStat/buildRebirthData에서 조회. */
  currentMultiplier(): Multiplier {
    return this.multSelector.current();
  }

  /**
   * 환생 버튼은 조건 미충족이어도 클릭 콜백을 열어 둔다.
   * 콜백에서 현재/필요 챕터를 토스트로 설명해야 하므로, 여기서는 비활성 시각 상태만 유지한다.
   */
  private setRebirthButtonAvailable(available: boolean): void {
    if (available) {
      setButtonEnabled(this.rebirthBtn, true);
      return;
    }
    this.rebirthBtn.setAlpha(DISABLED_BUTTON_ALPHA);
    this.rebirthBtn.setBgTint(PALETTE_NUM.surfaceDisabled);
    this.rebirthBtn.setBgAlpha(DISABLED_BUTTON_BG_ALPHA);
    this.rebirthBtn.setEnabled(true);
  }

  /** 언어 변경 — 정적 제목/하단 버튼만 갱신 (행 텍스트·버튼 비용 라벨은 update()의 t() 보간으로). */
  applyLanguage(): void {
    this.title.setText("");
    this.tipView.applyLanguage();
    this.multSelector.applyLanguage();
    this.renderRebirthButtonLabel(this.lastRequiredChapter);
  }

  private renderRebirthButtonLabel(requiredChapter: number): void {
    if (requiredChapter <= 0) {
      this.rebirthBtnPrefix.setText(t("rebirth.do"));
      this.rebirthBtnPrefix.setPosition(-this.rebirthBtnPrefix.width / 2, 0);
      this.rebirthBtnRequiredIcon.setVisible(false);
      this.rebirthBtnRequired.setVisible(false);
      return;
    }

    this.rebirthBtnRequiredIcon.setVisible(true);
    this.rebirthBtnRequired.setVisible(true);
    this.rebirthBtnPrefix.setText(rebirthButtonPrefixLabel());
    this.rebirthBtnRequired.setText(rebirthButtonRequiredLabel(requiredChapter));

    const groupW =
      this.rebirthBtnPrefix.width +
      REBIRTH_BUTTON_ICON_LEADING_GAP +
      REBIRTH_BUTTON_REQUIRED_ICON_SIZE +
      REBIRTH_BUTTON_ICON_VALUE_GAP +
      this.rebirthBtnRequired.width;
    const left = -groupW / 2;
    this.rebirthBtnPrefix.setPosition(left, 0);
    this.rebirthBtnRequiredIcon.setPosition(
      left + this.rebirthBtnPrefix.width + REBIRTH_BUTTON_ICON_LEADING_GAP + REBIRTH_BUTTON_REQUIRED_ICON_SIZE / 2,
      0,
    );
    this.rebirthBtnRequired.setPosition(
      left +
        this.rebirthBtnPrefix.width +
        REBIRTH_BUTTON_ICON_LEADING_GAP +
        REBIRTH_BUTTON_REQUIRED_ICON_SIZE +
        REBIRTH_BUTTON_ICON_VALUE_GAP,
      0,
    );
  }

  /**
   * 방향 전환(RTL) — computeRebirthLayout 재호출로 헤더/하단버튼 + 스크롤 행 좌표·origin·wrapWidth 재배치.
   * 스크롤 행은 ScrollableList.relayoutContent로 lx/ly 교체(destroy 0). scrollList 자체 위치는 전체 폭이라 dir 무관.
   */
  relayout(dir: TextDirection): void {
    const L = computeRebirthLayout({ ...this.rect, height: 0 }, dir, STAT_IDS.length, this.panelBottom);
    const headAlign = dir === "rtl" ? "right" : "left";
    this.title.setPosition(L.title.x, L.title.y).setOrigin(L.headTextOriginX, 0);
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    this.multSelector.setPosition(L.multiplier.x, L.multiplier.y);
    this.multSelector.relayout(dir);
    this.info.setPosition(L.info.x, L.info.y).setOrigin(L.headTextOriginX, 0).setAlign(headAlign);
    this.currencyChip.bg.setPosition(L.currencyChip.bg.x, L.currencyChip.bg.y);
    this.currencyChip.icon.setPosition(L.currencyChip.icon.x, L.currencyChip.icon.y);
    this.currencyChip.label
      .setPosition(L.currencyChip.label.x, L.currencyChip.label.y)
      .setOrigin(L.currencyChip.label.originX, 0.5);
    this.rebirthBtn.setPosition(L.rebirthBtn.x, L.rebirthBtn.y);
    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.rows.forEach((row, i) => {
      const r = L.rows[i]!;
      row.name.setOrigin(L.rowTextOriginX, 0.5).setWordWrapWidth(r.wrapWidth);
      row.breakdown.setOrigin(L.rowTextOriginX, 0.5).setWordWrapWidth(r.wrapWidth);
      row.result.setOrigin(L.rowTextOriginX, 0.5).setWordWrapWidth(r.wrapWidth);
      positions.push(
        { obj: row.frame, lx: r.frame.x, ly: r.frame.y },
        { obj: row.icon, lx: r.icon.x, ly: r.icon.y },
        { obj: row.name, lx: r.name.x, ly: r.name.y },
        { obj: row.breakdown, lx: r.breakdown.x, ly: r.breakdown.y },
        { obj: row.result, lx: r.result.x, ly: r.result.y },
        { obj: row.button, lx: r.button.x, ly: r.button.y },
      );
    });
    this.scrollList.relayoutContent(positions);
  }

  /** 셸 펼치기/접기 — 콘텐츠 rect(상단) 갱신 + 스크롤 뷰포트 확장 후 relayout으로 제목/info/행 재배치(항목 더 보임). */
  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    const dir = getTextDirection();
    const L = computeRebirthLayout({ ...this.rect, height: 0 }, dir, STAT_IDS.length, this.panelBottom);
    this.scrollList.resize(L.scroll.x, L.scroll.y, L.scroll.width, L.scroll.height);
    this.relayout(dir);
  }

  setVisible(visible: boolean): void {
    this.title.setVisible(visible);
    this.tipView.setVisible(visible);
    this.info.setVisible(visible);
    this.multSelector.setVisible(visible);
    this.currencyChip.bg.setVisible(visible);
    this.currencyChip.icon.setVisible(visible);
    this.currencyChip.label.setVisible(visible);
    this.scrollList.setVisible(visible);
    this.rebirthBtn.setVisible(visible);
  }
}
